// client/src/components/admin/AdminFeedback.js
import { useEffect, useState } from 'react'
import ApiFeedback from '../../api/ApiFeedback'
import PageLayout from '../common/layout/PageLayout'
import './AdminFeedback.css'

function AdminFeedback() {
  const [feedback, setFeedback] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all') // 'all', 'unread', 'archived'
  const [readItems, setReadItems] = useState(new Set())
  const [archivedItems, setArchivedItems] = useState(new Set())

  useEffect(() => {
    async function load() {
      try {
        const data = await ApiFeedback.getAll()
        setFeedback(data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    const storedRead = localStorage.getItem('feedbackReadItems')
    if (storedRead) {
      setReadItems(new Set(JSON.parse(storedRead)))
    }
    const storedArchived = localStorage.getItem('feedbackArchivedItems')
    if (storedArchived) {
      setArchivedItems(new Set(JSON.parse(storedArchived)))
    }
  }, [])

  const handleMarkAsRead = (id) => {
    setReadItems((prev) => {
      const next = new Set(prev)
      next.add(id)
      localStorage.setItem('feedbackReadItems', JSON.stringify([...next]))
      return next
    })
  }

  const handleArchive = (id) => {
    setArchivedItems((prev) => {
      const next = new Set(prev)
      next.add(id)
      localStorage.setItem('feedbackArchivedItems', JSON.stringify([...next]))
      return next
    })
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return
    }
    try {
      await ApiFeedback.delete(id)
      setFeedback((prev) => prev.filter((f) => f.id !== id))
    } catch (err) {
      console.error('Failed to delete feedback:', err)
      alert('Failed to delete feedback: ' + (err.message || 'Unknown error'))
    }
  }

  const copyToClipboard = (text) => {
    if (!navigator.clipboard) {
      alert('Clipboard not supported. Please copy manually: ' + text)
      return
    }
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // Simple visual feedback
        const btn = document.activeElement
        const originalText = btn.textContent
        btn.textContent = 'Copied!'
        setTimeout(() => {
          btn.textContent = originalText
        }, 1000)
      })
      .catch(() => {
        alert('Failed to copy to clipboard')
      })
  }

  const filteredFeedback = feedback.filter((f) => {
    if (archivedItems.has(f.id)) {
      return filter === 'archived'
    }
    if (filter === 'unread') {
      return !readItems.has(f.id)
    }
    return filter === 'all'
  })

  if (loading)
    return (
      <PageLayout title="User Feedback" noCard={true}>
        <p>Loading...</p>
      </PageLayout>
    )

  if (error)
    return (
      <PageLayout title="User Feedback" noCard={true}>
        <p className="text-danger">{error}</p>
      </PageLayout>
    )

  return (
    <PageLayout title="User Feedback" noCard={true}>
      <div className="admin-feedback-container">
        {/* Filter Buttons */}
        <div className="feedback-filters">
          <div className="btn-group" role="group" aria-label="Filter feedback">
            <button
              type="button"
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('all')}
              data-testid="filter-all"
            >
              All ({feedback.length - archivedItems.size})
            </button>
            <button
              type="button"
              className={`btn ${filter === 'unread' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('unread')}
              data-testid="filter-unread"
            >
              Unread (
              {
                feedback.filter(
                  (f) => !readItems.has(f.id) && !archivedItems.has(f.id),
                ).length
              }
              )
            </button>
            <button
              type="button"
              className={`btn ${filter === 'archived' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('archived')}
              data-testid="filter-archived"
            >
              Archived ({archivedItems.size})
            </button>
          </div>
        </div>

        {/* Feedback List */}
        {filteredFeedback.length === 0 ? (
          <div className="empty-feedback-state">
            <i className="bi bi-inbox"></i>
            <h5>
              {filter === 'unread' && 'No Unread Feedback'}
              {filter === 'archived' && 'No Archived Feedback'}
              {filter === 'all' && 'No Feedback Yet'}
            </h5>
            <p className="text-muted">
              {filter === 'unread' &&
                'All feedback has been marked as read. Great job!'}
              {filter === 'archived' && "You haven't archived any feedback."}
              {filter === 'all' &&
                'User feedback will appear here when submitted.'}
            </p>
          </div>
        ) : (
          filteredFeedback.map((f) => {
            const isUnread = !readItems.has(f.id)
            return (
              <div
                key={f.id}
                className={`feedback-card ${isUnread ? 'unread' : ''}`}
                data-testid={`feedback-card-${f.id}`}
              >
                <div className="feedback-card-header">
                  <div className="feedback-user-info">
                    <div className="feedback-email">
                      {f.email || 'Unknown User'}
                    </div>
                    <div className="feedback-date">
                      {f.submitted_at
                        ? new Date(f.submitted_at).toLocaleString()
                        : f.created_at
                          ? new Date(f.created_at).toLocaleString()
                          : 'N/A'}
                    </div>
                  </div>
                  <div className="feedback-id">ID: {f.id}</div>
                </div>

                <div className="feedback-message">{f.message}</div>

                {f.page_url && (
                  <div className="feedback-page-url">
                    <span className="feedback-page-url-label">Page URL:</span>
                    {f.page_url}
                  </div>
                )}

                <div className="feedback-actions">
                  {isUnread && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleMarkAsRead(f.id)}
                      data-testid={`mark-read-${f.id}`}
                      aria-label={`Mark feedback ${f.id} from ${f.email || 'unknown user'} as read`}
                    >
                      <i className="bi bi-check2" aria-hidden="true"></i> Mark
                      as Read
                    </button>
                  )}
                  {!archivedItems.has(f.id) && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleArchive(f.id)}
                      data-testid={`archive-${f.id}`}
                      aria-label={`Archive feedback ${f.id} from ${f.email || 'unknown user'}`}
                    >
                      <i className="bi bi-archive" aria-hidden="true"></i>{' '}
                      Archive
                    </button>
                  )}
                  {f.email && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => copyToClipboard(f.email)}
                      data-testid={`copy-email-${f.id}`}
                      aria-label={`Copy email address ${f.email} to clipboard`}
                    >
                      <i className="bi bi-envelope" aria-hidden="true"></i> Copy
                      Email
                    </button>
                  )}
                  {f.page_url && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => copyToClipboard(f.page_url)}
                      data-testid={`copy-url-${f.id}`}
                      aria-label={`Copy page URL ${f.page_url} to clipboard`}
                    >
                      <i className="bi bi-link-45deg" aria-hidden="true"></i>{' '}
                      Copy URL
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(f.id)}
                    data-testid={`delete-${f.id}`}
                    aria-label={`Delete feedback ${f.id} from ${f.email || 'unknown user'}`}
                  >
                    <i className="bi bi-trash" aria-hidden="true"></i> Delete
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </PageLayout>
  )
}

export default AdminFeedback
